import React, { useState } from 'react';

const BoxConfigurator = () => {
  // Zustand für die Kartonmaße in Zentimetern
  const [breite, setBreite] = useState(30);
  const [tiefe, setTiefe] = useState(20);
  const [hoehe, setHoehe] = useState(15);
  const [rotation, setRotation] = useState({ x: 15, y: 45 });

  // Container-Dimensionen berechnen
  const containerHeight = 600; // Höhere Container für bessere Visualisierung
  const containerWidth = 800;  

  // Berechnung eines optimalen Skalierungsfaktors
  const maxDimension = Math.max(breite, tiefe, hoehe);
  const scaleFactor = Math.min(containerHeight * 0.6 / maxDimension, containerWidth * 0.6 / maxDimension);

  // Handler für Rotation mit der Maus
  const handleMouseMove = (e) => {
    if (e.buttons === 1) { // Linke Maustaste gedrückt
      setRotation({
        x: Math.min(80, Math.max(-10, rotation.x - e.movementY * 0.5)),
        y: rotation.y + e.movementX * 0.5
      });
    }
  };

  // Handler für Touchscreens
  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      if (!e.currentTarget.lastTouch) {
        e.currentTarget.lastTouch = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
        return;
      }

      const deltaX = e.touches[0].clientX - e.currentTarget.lastTouch.x;
      const deltaY = e.touches[0].clientY - e.currentTarget.lastTouch.y;
      
      setRotation({
        x: Math.min(80, Math.max(-10, rotation.x - deltaY * 0.5)),
        y: rotation.y + deltaX * 0.5
      });
      
      e.currentTarget.lastTouch = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
  };
  
  const handleTouchEnd = (e) => {
    e.currentTarget.lastTouch = null;
  };

  // Wandstärke für den Deckel
  const wandstaerke = 0.5; // in cm

  // Farben
  const boxColor = '#e18632'; // Orange
  const deckelColor = '#8a8a8a'; // Grau
  
  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>Karton-Konfigurator</h2>
      <p style={{ margin: '0 0 20px 0', fontSize: '16px' }}>
        Passen Sie die Maße Ihres Kartons an und sehen Sie das Ergebnis in Echtzeit.
      </p>
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        flexWrap: 'wrap',
        gap: '30px',
        alignItems: 'stretch'
      }}>
        {/* Eingabebereich */}
        <div style={{ 
          flex: '1', 
          minWidth: '250px',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: '0', fontSize: '20px' }}>Maße</h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Breite (cm):
              <input
                type="number"
                value={breite}
                onChange={(e) => setBreite(Math.max(1, Math.min(100, Number(e.target.value))))}
                style={{ 
                  marginLeft: '15px', 
                  width: '80px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </label>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Tiefe (cm):
              <input
                type="number"
                value={tiefe}
                onChange={(e) => setTiefe(Math.max(1, Math.min(100, Number(e.target.value))))}
                style={{ 
                  marginLeft: '15px', 
                  width: '80px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </label>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Höhe (cm):
              <input
                type="number"
                value={hoehe}
                onChange={(e) => setHoehe(Math.max(1, Math.min(100, Number(e.target.value))))}
                style={{ 
                  marginLeft: '15px', 
                  width: '80px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </label>
          </div>
          
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#ebebeb', 
            borderRadius: '5px',
            fontSize: '14px', 
            color: '#555'
          }}>
            <p style={{ margin: '0 0 10px 0' }}>
              Geben Sie die gewünschten Maße ein (1-100 cm).
            </p>
            <p style={{ margin: '0' }}>
              <strong>Tipp:</strong> Klicken und ziehen Sie, um den Karton zu drehen.
            </p>
          </div>
        </div>

        {/* Visualisierungsbereich */}
        <div style={{ 
          flex: '2', 
          minWidth: '350px',
          height: `${containerHeight}px`, 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#f9f9f9',
          boxShadow: 'inset 0 0 50px rgba(0,0,0,0.05)',
          position: 'relative'
        }}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        >
          {/* 3D-Box Container */}
          <div style={{
            width: '100%', 
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            perspective: '1500px',
            cursor: 'grab',
            position: 'relative'
          }}>
            {/* 3D Karton */}
            <div style={{
              width: `${breite * scaleFactor}px`,
              height: `${hoehe * scaleFactor}px`,
              position: 'relative',
              transformStyle: 'preserve-3d',
              transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
              transition: 'transform 0.1s ease'
            }}>
              {/* Boden */}
              <div style={{
                position: 'absolute',
                width: `${breite * scaleFactor}px`,
                height: `${tiefe * scaleFactor}px`,
                background: boxColor,
                transform: `rotateX(90deg) translateZ(${hoehe * scaleFactor / 2}px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.15)'
              }}>
                Boden
              </div>
              
              {/* Deckel (oben) */}
              <div style={{
                position: 'absolute',
                width: `${(breite + wandstaerke*2) * scaleFactor}px`,
                height: `${(tiefe + wandstaerke*2) * scaleFactor}px`,
                background: deckelColor,
                transform: `rotateX(90deg) translateZ(${-hoehe * scaleFactor / 2 - wandstaerke * scaleFactor}px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#555',
                boxShadow: '0 0 15px rgba(0,0,0,0.2)'
              }}>
                Deckel
              </div>
              
              {/* Vorderseite */}
              <div style={{
                position: 'absolute',
                width: `${breite * scaleFactor}px`,
                height: `${hoehe * scaleFactor}px`,
                background: boxColor,
                transform: `translateZ(${tiefe * scaleFactor / 2}px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.15)'
              }}>
                Vorderseite
              </div>
              
              {/* Rückseite */}
              <div style={{
                position: 'absolute',
                width: `${breite * scaleFactor}px`,
                height: `${hoehe * scaleFactor}px`,
                background: boxColor,
                transform: `translateZ(${-tiefe * scaleFactor / 2}px) rotateY(180deg)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.15)'
              }}>
                Rückseite
              </div>
              
              {/* Rechte Seite */}
              <div style={{
                position: 'absolute',
                width: `${tiefe * scaleFactor}px`,
                height: `${hoehe * scaleFactor}px`,
                background: boxColor,
                transform: `rotateY(90deg) translateZ(${breite * scaleFactor / 2}px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.15)'
              }}>
                Rechts
              </div>
              
              {/* Linke Seite */}
              <div style={{
                position: 'absolute',
                width: `${tiefe * scaleFactor}px`,
                height: `${hoehe * scaleFactor}px`,
                background: boxColor,
                transform: `rotateY(-90deg) translateZ(${breite * scaleFactor / 2}px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.15)'
              }}>
                Links
              </div>
            </div>
          </div>
          
          {/* Maßanzeige */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            fontSize: '16px',
            fontWeight: 'bold',
            background: 'rgba(255,255,255,0.8)',
            padding: '8px 15px',
            borderRadius: '5px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            color: '#333',
            zIndex: 10
          }}>
            {breite} × {tiefe} × {hoehe} cm
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxConfigurator; 